import { Link } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { ErrorState, Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney } from '@/lib/money'

export function AccountsScreen() {
  const repository = useRepository()
  const accounts = useQuery(() => repository.listAccounts(), [repository])

  return (
    <>
      <PageHeader
        title="Accounts"
        subtitle="Real local details in your name — not a pooled account."
        actions={
          <Button variant="primary" size="md" iconStart="add" to="/open/account">
            Open an account
          </Button>
        }
      />

      {accounts.status === 'error' && <ErrorState message="We couldn't load your accounts." />}

      <BentoGrid>
        {accounts.status === 'loading' &&
          [0, 1, 2, 3].map((key) => (
            <BentoSpan key={key} span={2}>
              <Skeleton height={168} />
            </BentoSpan>
          ))}

        {accounts.data?.map((account) => (
          <BentoSpan key={account.id} span={2}>
            <Card tone={account.primary ? 'dark' : 'light'}>
              <Card.Header>
                <Stack direction="row" align="center" gap={3}>
                  <CurrencyBadge currency={account.currency} size={40} />
                  <Stack gap={0}>
                    <Text
                      variant="caption"
                      weight="semibold"
                      tone={account.primary ? 'onDark' : 'primary'}
                    >
                      {account.label}
                    </Text>
                    <Text
                      variant="eyebrow"
                      tone={account.primary ? 'onDarkMuted' : 'secondary'}
                    >
                      {account.rail} · {account.maskedNumber}
                    </Text>
                  </Stack>
                </Stack>
                {account.status === 'awaiting-deposit' ? (
                  <Pill tone="warning">Awaiting deposit</Pill>
                ) : (
                  account.primary && <Pill tone="accent">Primary</Pill>
                )}
              </Card.Header>

              <Stack gap={1} className="pb-mt-5">
                <Text
                  as="p"
                  variant="title"
                  weight="bold"
                  numeric
                  tone={account.primary ? 'onDark' : 'primary'}
                >
                  {formatMoney(account.balance)}
                </Text>
                <Text variant="caption" tone={account.primary ? 'onDarkMuted' : 'secondary'}>
                  {account.status === 'awaiting-deposit' ? 'Not active yet' : 'Available now'}
                </Text>
              </Stack>

              <Card.Footer>
                {account.status === 'awaiting-deposit' ? (
                  <Stack gap={3}>
                    <Text variant="caption" tone="secondary">
                      The details are already yours. It starts working with a first deposit of $50,
                      which stays in the account.
                    </Text>
                    <Button
                      size="md"
                      block
                      iconEnd="arrow_forward"
                      to={`/activate/account/${account.id}`}
                    >
                      Activate this account
                    </Button>
                  </Stack>
                ) : (
                <Stack direction="row" gap={2} wrap>
                  <Button variant={account.primary ? 'primary' : 'outline'} size="sm" iconStart="south_west" to="/receive">
                    Receive
                  </Button>
                  <Button variant={account.primary ? 'onDark' : 'outline'} size="sm" iconStart="north_east" to="/transfers/send">
                    Send
                  </Button>
                  <Button variant={account.primary ? 'onDark' : 'outline'} size="sm" iconStart="currency_exchange" to="/convert">
                    Convert
                  </Button>
                  <Button
                    variant={account.primary ? 'onDark' : 'outline'}
                    size="sm"
                    iconEnd="arrow_forward"
                    to={`/accounts/${account.id}`}
                  >
                    Details
                  </Button>
                </Stack>
                )}
              </Card.Footer>
            </Card>
          </BentoSpan>
        ))}

        <BentoSpan span={4}>
          <Card tone="plain" interactive as={Link} to="/open/account">
            <Stack direction="row" align="center" gap={4}>
              <Icon name="add_circle" size={24} />
              <Stack grow gap={0}>
                <Text variant="caption" weight="semibold">
                  Open another local account
                </Text>
                <Text variant="caption" tone="secondary">
                  Free to open. Start it with $50, which stays yours. USD, EUR, MXN and BRL.
                </Text>
              </Stack>
              <Icon name="arrow_forward" size={18} />
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
